import { useEffect } from 'preact/hooks';

// Alternative to the built-in %-operator for modulus operations.
// The %-operator handles negative numbers in an unexpected way.
// http://stackoverflow.com/a/17323608/89594

// Further extended to allow passing an offset to allow sequences
// starting from a different number than 0.
/**
 * @param n dividend
 * @param m divisor
 * @param o offset
 * @returns {number}
 */
export function mod(n, m, o = 0) {
  return ((((n - o) % m) + m) % m) + o;
}

const KEYCODE = {
  RETURN: 13,
  ESC: 27,
  SPACE: 32,
  UP: 38,
  DOWN: 40,
};

// Determine if an element is visible.
// https://stackoverflow.com/a/21696585/89594.
const isVisible = el => !!el.offsetParent;

let previouslyFocused;

const TAB_INDEX = 'tabindex';

const FOCUS_MOVEMENT = {
  [KEYCODE.UP]: -1,
  [KEYCODE.DOWN]: 1,
};

// Implementation of roving tabindex per:
// https://www.w3.org/TR/wai-aria-practices-1.1/#kbd_roving_tabindex
function moveRovingFocus(el, amount) {
  const tabindexs = Array.from(el.querySelectorAll(`[${TAB_INDEX}]`)).filter(
    isVisible
  );

  if (tabindexs.length === 0) {
    return;
  }

  const currentIndex = tabindexs.findIndex(
    x => x.getAttribute(TAB_INDEX) === '0'
  );
  let nextIndex;

  if (currentIndex !== -1) {
    const current = tabindexs[currentIndex];
    current.setAttribute(TAB_INDEX, '-1');
    nextIndex = mod(currentIndex + amount, tabindexs.length);
  } else {
    nextIndex = 0;
  }

  const next = tabindexs[nextIndex];
  next.setAttribute(TAB_INDEX, '0');
  next.focus();
}

function captureFocus(el) {
  previouslyFocused = document.activeElement;
  // Wait for other blur/focus events that might interrupt capturing.
  setTimeout(() => moveRovingFocus(el, 0));
}

// Partial implementation of ARIA Menu Keyboard Interaction spec:
// https://www.w3.org/TR/wai-aria-practices-1.1/#menu
const _handleKeyDown = el => event => {
  switch (event.keyCode) {
    case KEYCODE.DOWN:
    case KEYCODE.UP:
      moveRovingFocus(el, FOCUS_MOVEMENT[event.keyCode]);
      // Prevent arrow keys from scrolling page.
      event.preventDefault();
      break;
    case KEYCODE.RETURN:
    case KEYCODE.ESC:
      // Wait so we don't interrupt the browser's native RETURN handler
      // (which triggers click behavior on buttons/anchors/etc).
      setTimeout(() => restoreFocus());
      break;
  }
};

function handleClick(event) {
  const targetTabIndex = event.target.getAttribute(TAB_INDEX);

  // When a menu item is clicked (activated, same as RETURN), the menu
  // is considered closed and should restore focus to the element from
  // which it was captured.
  if (['-1', '0'].indexOf(targetTabIndex) !== -1) {
    restoreFocus();
  }
}

function restoreFocus(el) {
  const focusedElement = document.activeElement;

  // Don't attempt to restore focus if something else already moved it.
  if (el?.contains(focusedElement)) {
    focusedElement.blur();

    // `previouslyFocused` can be null (since `document.activeElement` can
    // be `null`), though we're only seeing this in IE11.
    // https://developer.mozilla.org/en-US/docs/Web/API/DocumentOrShadowRoot/activeElement#Value
    if (previouslyFocused) {
      previouslyFocused.focus();
    }
  }
}

/**
 * Manages mouse and keyboard focus for a menu per ARIA specs (see comments below).
 *
 * Usage:
 * useMenuFocus({
 *   enabled: true,
 *   ref,
 * });
 * <ul ref={ref}>
 *   <li tabindex="-1">1</li>
 *   <li tabindex="-1">2</li>
 *   <li tabindex="-1">3</li>
 * </ul>
 *
 * Accepts an expression that toggles the directive's functionality.
 * Once toggled, it will capture focus within its element. All child elements with
 * `tabindex="-1"` will be focusable using arrow keys using an implementation of
 * "roving" keyboard focus.
 */
export const useMenuFocus = ({ enabled, ref }) => {
  useEffect(() => {
    const handleKeyDown = _handleKeyDown(ref.current);
    if (enabled && ref.current?.addEventListener) {
      captureFocus(ref.current);
      ref.current.addEventListener('keydown', handleKeyDown);
      ref.current.addEventListener('click', handleClick);
    }
    return () => {
      ref.current?.removeEventListener('keydown', handleKeyDown);
      ref.current?.removeEventListener('click', handleClick);
      restoreFocus(ref.current);
    };
  }, [ref, enabled]);
};
